import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { authApi } from '../api';
import { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import versionInfo from '../version.json';
import {
  LayoutDashboard,
  Building2,
  FileText,
  Banknote,
  ClipboardList,
  Users,
  LogOut,
  Printer,
  UserCheck,
  Landmark,
  Settings,
  XCircle,
  Eye,
  ArrowRightLeft,
  FileCheck2,
  ChevronDown,
  ChevronRight,
  PanelLeftClose,
  PanelLeftOpen,
  Palette,
  KeyRound,
} from 'lucide-react';

const THEMES = {
  slate: { label: 'Slate', sidebar: '#1e293b', sidebarBorder: '#334155', accent: '#3b82f6', swatch: '#1e293b', headerBg: '#ffffff', contentBg: '#f0f4f8', panelBg: '#1e293b', panelText: '#ffffff', cardBorder: '#e2e8f0', accentLight: '#eff6ff', accentMuted: '#93c5fd' },
  navy: { label: 'Navy', sidebar: '#0f172a', sidebarBorder: '#1e3a5f', accent: '#3b82f6', swatch: '#0f172a', headerBg: '#f8fafc', contentBg: '#f1f5f9', panelBg: '#0f172a', panelText: '#e0f2fe', cardBorder: '#cbd5e1', accentLight: '#dbeafe', accentMuted: '#60a5fa' },
  charcoal: { label: 'Charcoal', sidebar: '#18181b', sidebarBorder: '#3f3f46', accent: '#a78bfa', swatch: '#18181b', headerBg: '#fafafa', contentBg: '#f4f4f5', panelBg: '#27272a', panelText: '#e4e4e7', cardBorder: '#d4d4d8', accentLight: '#f5f3ff', accentMuted: '#c4b5fd' },
  forest: { label: 'Forest', sidebar: '#14291e', sidebarBorder: '#1e4030', accent: '#22c55e', swatch: '#14291e', headerBg: '#f7fdf9', contentBg: '#f0fdf4', panelBg: '#14291e', panelText: '#bbf7d0', cardBorder: '#bbf7d0', accentLight: '#dcfce7', accentMuted: '#4ade80' },
  midnight: { label: 'Midnight', sidebar: '#0c1222', sidebarBorder: '#1c2842', accent: '#6366f1', swatch: '#0c1222', headerBg: '#f8f8ff', contentBg: '#eef2ff', panelBg: '#1e1b4b', panelText: '#c7d2fe', cardBorder: '#c7d2fe', accentLight: '#e0e7ff', accentMuted: '#818cf8' },
  espresso: { label: 'Espresso', sidebar: '#1c1410', sidebarBorder: '#3d2e24', accent: '#f59e0b', swatch: '#1c1410', headerBg: '#fffbf5', contentBg: '#fefce8', panelBg: '#292018', panelText: '#fde68a', cardBorder: '#fde68a', accentLight: '#fef9c3', accentMuted: '#fbbf24' },
};

const sectionColors = {
  Operations: '#60a5fa',
  Compliance: '#f97316',
  Setup: '#6ee7b7',
};

const NavDivider = ({ label, collapsed, onToggle }) => (
  <div className="px-3 py-2 mt-2">
    <button
      onClick={onToggle}
      className="flex items-center gap-2 w-full group"
      data-testid={`section-toggle-${label?.toLowerCase()}`}
    >
      <div className="h-px flex-1" style={{ backgroundColor: sectionColors[label] ? `${sectionColors[label]}33` : '#334155' }}></div>
      {label && (
        <span className="text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1 transition-colors" style={{ color: sectionColors[label] || '#94a3b8' }}>
          {label}
          {collapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </span>
      )}
      <div className="h-px flex-1" style={{ backgroundColor: sectionColors[label] ? `${sectionColors[label]}33` : '#334155' }}></div>
    </button>
  </div>
);

export const Layout = ({ children }) => {
  const { user, logout, isEmulating, stopEmulation, idleWarning } = useAuth();
  const navigate = useNavigate();
  const [footerText, setFooterText] = useState('');
  const [activeTheme, setActiveTheme] = useState(() => localStorage.getItem('app_theme') || 'slate');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showThemePicker, setShowThemePicker] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', newPw: '', confirm: '' });
  const [pwLoading, setPwLoading] = useState(false);
  const userMenuRef = useRef(null);
  const themePickerRef = useRef(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem('sidebar_collapsed') === 'true';
  });
  const [collapsedSections, setCollapsedSections] = useState(() => {
    try { return JSON.parse(localStorage.getItem('sidebar_sections_collapsed') || '{}'); }
    catch { return {}; }
  });

  const toggleSidebar = () => {
    setSidebarCollapsed(prev => {
      localStorage.setItem('sidebar_collapsed', !prev);
      return !prev;
    });
  };

  const toggleSection = (section) => {
    setCollapsedSections(prev => {
      const next = { ...prev, [section]: !prev[section] };
      localStorage.setItem('sidebar_sections_collapsed', JSON.stringify(next));
      return next;
    });
  };

  useEffect(() => {
    const theme = THEMES[activeTheme] || THEMES.slate;
    const root = document.documentElement.style;
    root.setProperty('--sidebar-bg', theme.sidebar);
    root.setProperty('--sidebar-border', theme.sidebarBorder);
    root.setProperty('--accent', theme.accent);
    root.setProperty('--header-bg', theme.headerBg);
    root.setProperty('--content-bg', theme.contentBg);
    root.setProperty('--panel-bg', theme.panelBg);
    root.setProperty('--panel-text', theme.panelText);
    root.setProperty('--card-border', theme.cardBorder);
    root.setProperty('--accent-light', theme.accentLight);
    root.setProperty('--accent-muted', theme.accentMuted);
  }, [activeTheme]);

  const applyTheme = (key) => {
    setActiveTheme(key);
    localStorage.setItem('app_theme', key);
    setShowThemePicker(false);
  };

  const handleChangePassword = async () => {
    if (pwForm.newPw !== pwForm.confirm) { toast.error('New passwords do not match'); return; }
    if (pwForm.newPw.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setPwLoading(true);
    try {
      await authApi.changePassword({ current_password: pwForm.current, new_password: pwForm.newPw });
      toast.success('Password changed successfully');
      setShowChangePassword(false);
      setPwForm({ current: '', newPw: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally { setPwLoading(false); }
  };

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (showUserMenu && userMenuRef.current && !userMenuRef.current.contains(e.target)) setShowUserMenu(false);
      if (showThemePicker && themePickerRef.current && !themePickerRef.current.contains(e.target)) setShowThemePicker(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showUserMenu, showThemePicker]);

  useEffect(() => {
    const fetchFooter = () => {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/app-settings/public`)
        .then(r => r.json())
        .then(d => {
          const parts = [];
          if (d.copyright_text) parts.push(d.copyright_text);
          parts.push(`Version ${versionInfo.version}`);
          setFooterText(parts.join(' \u00b7 '));
        })
        .catch(() => {
          setFooterText(`Version ${versionInfo.version}`);
        });
    };
    fetchFooter();
    window.addEventListener('footerSettingsChanged', fetchFooter);
    return () => window.removeEventListener('footerSettingsChanged', fetchFooter);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleStopEmulation = async () => {
    await stopEmulation();
    navigate('/users');
  };

  // Operations section - main actions
  const operationsItems = [
    { to: '/checks/create', icon: FileText, label: 'Create Check', iconColor: '#60a5fa' },
    { to: '/wires', icon: Banknote, label: 'Wire Instructions', iconColor: '#34d399' },
    { to: '/ach', icon: ArrowRightLeft, label: 'ACH Transfers', iconColor: '#a78bfa' },
    { to: '/payments', icon: ClipboardList, label: 'Payment Register', iconColor: '#f59e0b' },
  ];

  // Tax & Compliance
  const complianceItems = [
    { to: '/tax-compliance', icon: FileCheck2, label: 'Tax Compliance', iconColor: '#f97316' },
  ];

  // Management section - configuration/setup
  const managementItems = [
    { to: '/llcs', icon: Building2, label: 'LLC Management', iconColor: '#6ee7b7' },
    { to: '/users', icon: Users, label: 'User Management', superadminOnly: true, iconColor: '#93c5fd' },
    { to: '/banks', icon: Landmark, label: 'Bank Management', iconColor: '#fbbf24' },
    { to: '/payees', icon: UserCheck, label: 'Payee Management', iconColor: '#c4b5fd' },
    { to: '/settings', icon: Settings, label: 'Settings', superadminOnly: true, iconColor: '#94a3b8' },
  ];

  const renderNavItem = ({ to, icon: Icon, label, superadminOnly, iconColor }, _idx, _arr, sectionKey) => {
    if (superadminOnly && user?.role !== 'superadmin') return null;
    return (
      <div
        key={to}
        draggable={!sidebarCollapsed}
        onDragStart={() => setDragItem({ section: sectionKey, to })}
        onDragOver={(e) => e.preventDefault()}
        onDrop={() => handleItemDrop(sectionKey, to)}
        className={`${dragItem?.to === to ? 'opacity-50' : ''}`}
      >
        <NavLink
          to={to}
          end={to === '/'}
          className={({ isActive }) =>
            `nav-item ${isActive ? 'active' : ''}`
          }
          data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
          title={sidebarCollapsed ? label : undefined}
        >
          <Icon className="w-5 h-5" style={{ color: iconColor }} />
          {!sidebarCollapsed && <span>{label}</span>}
        </NavLink>
      </div>
    );
  };

  // Item ordering within sections
  const [itemOrders, setItemOrders] = useState(() => {
    try { return JSON.parse(localStorage.getItem('sidebar_item_orders') || '{}'); }
    catch { return {}; }
  });
  const [dragItem, setDragItem] = useState(null);

  const getOrderedItems = (sectionKey, items) => {
    const order = itemOrders[sectionKey];
    if (!order) return items;
    const itemMap = {};
    items.forEach(item => { itemMap[item.to] = item; });
    const ordered = order.filter(to => itemMap[to]).map(to => itemMap[to]);
    items.forEach(item => { if (!order.includes(item.to)) ordered.push(item); });
    return ordered;
  };

  const handleItemDrop = (sectionKey, targetTo) => {
    if (!dragItem || dragItem.section !== sectionKey || dragItem.to === targetTo) {
      setDragItem(null);
      return;
    }
    const section = sectionMap[sectionKey];
    const currentItems = getOrderedItems(sectionKey, section.items);
    const currentOrder = currentItems.map(i => i.to);
    const fromIdx = currentOrder.indexOf(dragItem.to);
    const toIdx = currentOrder.indexOf(targetTo);
    currentOrder.splice(fromIdx, 1);
    currentOrder.splice(toIdx, 0, dragItem.to);
    const next = { ...itemOrders, [sectionKey]: currentOrder };
    setItemOrders(next);
    localStorage.setItem('sidebar_item_orders', JSON.stringify(next));
    setDragItem(null);
  };

  const sectionMap = {
    operations: { label: 'Operations', items: operationsItems },
    compliance: { label: 'Compliance', items: complianceItems },
    setup: { label: 'Setup', items: managementItems },
  };

  const sections = user?.role === 'clerk'
    ? ['operations']
    : ['operations', 'compliance', 'setup'];

  return (
    <div className="app-layout">
      {/* Emulation Banner */}
      {isEmulating && (
        <div className="fixed top-0 left-0 right-0 bg-amber-500 text-white px-4 py-2 z-50 flex items-center justify-between no-print">
          <div className="flex items-center gap-2">
            <Eye className="w-5 h-5" />
            <span className="font-medium">
              Viewing as: {user?.name} ({user?.email})
            </span>
            <span className="text-amber-100 text-sm ml-2">
              Role: {user?.role}
            </span>
          </div>
          <Button
            size="sm"
            variant="secondary"
            onClick={handleStopEmulation}
            className="bg-white text-amber-600 hover:bg-amber-50"
            data-testid="stop-emulation-btn"
          >
            <XCircle className="w-4 h-4 mr-2" />
            Stop Emulating
          </Button>
        </div>
      )}

      {/* Idle Warning Banner */}
      {idleWarning && !isEmulating && (
        <div className="fixed top-0 left-0 right-0 bg-red-600 text-white px-4 py-2 z-50 flex items-center justify-center gap-3 no-print" data-testid="idle-warning-banner">
          <span className="font-medium">Session expiring soon due to inactivity — move your mouse or press a key to stay logged in</span>
        </div>
      )}

      {/* Sidebar */}
      <aside className={`sidebar no-print ${isEmulating ? 'mt-12' : ''} ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <div className="sidebar-header" style={{ justifyContent: sidebarCollapsed ? 'center' : undefined, padding: sidebarCollapsed ? '16px 8px' : undefined }}>
          {sidebarCollapsed ? (
            <img src="/logo.png" alt="PMG" className="w-9 h-9 rounded-lg object-contain" />
          ) : (
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="PayMasterGeneral" className="w-10 h-10 rounded-lg object-contain" />
              <div>
                <h1 className="font-semibold text-slate-900 text-lg">PayMasterGeneral</h1>
                <p className="text-xs text-slate-500">Payments. Simplified.</p>
              </div>
            </div>
          )}
        </div>

        <nav className="sidebar-nav">
          <div className="space-y-1">
            {/* Dashboard */}
            <NavLink
              to="/"
              end
              className={({ isActive }) =>
                `nav-item ${isActive ? 'active' : ''}`
              }
              data-testid="nav-dashboard"
              title={sidebarCollapsed ? 'Dashboard' : undefined}
            >
              <LayoutDashboard className="w-5 h-5" style={{ color: '#38bdf8' }} />
              {!sidebarCollapsed && <span>Dashboard</span>}
            </NavLink>

            {/* Sections with reorderable items */}
            {sections.map(key => {
              const section = sectionMap[key];
              if (!section) return null;
              const isCollapsed = collapsedSections[key];
              const orderedItems = getOrderedItems(key, section.items);
              return (
                <div key={key}>
                  {sidebarCollapsed ? (
                    <div className="h-px mx-3 my-2 bg-slate-200" />
                  ) : (
                    <NavDivider
                      label={section.label}
                      collapsed={isCollapsed}
                      onToggle={() => toggleSection(key)}
                    />
                  )}
                  {!isCollapsed && orderedItems.map((item, idx, arr) => renderNavItem(item, idx, arr, key))}
                </div>
              );
            })}
          </div>
        </nav>

        {/* Sidebar collapse toggle */}
        <div className="mt-auto border-t border-slate-200 p-2">
          <button
            onClick={toggleSidebar}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors text-sm"
            data-testid="sidebar-collapse-btn"
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCollapsed ? <PanelLeftOpen className="w-5 h-5" /> : <><PanelLeftClose className="w-5 h-5" /><span>Collapse</span></>}
          </button>
        </div>
      </aside>

      {/* Main Content Area with Header */}
      <div className={`main-content-wrapper ${isEmulating ? 'mt-12' : ''}`}>
        {/* Top Header Bar */}
        <header className="top-header no-print">
          <div className="flex-1"></div>
          <div className="flex items-center gap-3">
            {/* Theme Picker */}
            <div className="relative" ref={themePickerRef}>
              <button
                onClick={() => { setShowThemePicker(!showThemePicker); setShowUserMenu(false); }}
                className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
                data-testid="theme-picker-btn"
                title="Change theme"
              >
                <Palette className="w-5 h-5 text-slate-500" />
              </button>
              {showThemePicker && (
                <div className="fixed right-24 top-14 bg-white rounded-lg shadow-xl border p-3 z-50 w-48" data-testid="theme-picker-dropdown">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Theme</p>
                  <div className="space-y-1">
                    {Object.entries(THEMES).map(([key, theme]) => (
                      <button
                        key={key}
                        onClick={() => applyTheme(key)}
                        className={`w-full flex items-center gap-3 px-2 py-1.5 rounded-md text-sm transition-colors ${activeTheme === key ? 'bg-slate-100 font-medium' : 'hover:bg-slate-50'}`}
                        data-testid={`theme-${key}`}
                      >
                        <div className="w-5 h-5 rounded-full border-2 border-white shadow-sm" style={{ backgroundColor: theme.swatch }} />
                        <span className="text-slate-700">{theme.label}</span>
                        {activeTheme === key && <span className="ml-auto text-blue-500 text-xs">Active</span>}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* User Menu */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => { setShowUserMenu(!showUserMenu); setShowThemePicker(false); }}
                className="flex items-center gap-3 hover:bg-slate-50 rounded-lg px-2 py-1 transition-colors"
                data-testid="user-menu-btn"
              >
                <div className="text-right">
                  <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                  <p className="text-xs text-slate-500">{user?.email}</p>
                </div>
                <div className={`w-9 h-9 rounded-full flex items-center justify-center ${isEmulating ? 'bg-amber-100' : 'bg-slate-200'}`}>
                  <span className={`text-sm font-medium ${isEmulating ? 'text-amber-600' : 'text-slate-600'}`}>
                    {user?.name?.charAt(0).toUpperCase() || 'U'}
                  </span>
                </div>
              </button>
              {showUserMenu && (
                <div className="fixed right-4 top-14 bg-white rounded-lg shadow-xl border py-1 z-50 w-52" data-testid="user-menu-dropdown">
                  <button
                    onClick={() => { setShowChangePassword(true); setShowUserMenu(false); }}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                    data-testid="change-password-btn"
                  >
                    <KeyRound className="w-4 h-4 text-slate-400" />
                    Change Password
                  </button>
                  <div className="border-t my-1" />
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    data-testid="logout-menu-btn"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Change Password Modal */}
        {showChangePassword && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowChangePassword(false)}>
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-sm mx-4" onClick={e => e.stopPropagation()} data-testid="change-password-modal">
              <h2 className="text-lg font-semibold text-slate-900 mb-1">Change Password</h2>
              <p className="text-sm text-slate-500 mb-4">Enter your current password and choose a new one</p>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-slate-700">Current Password</label>
                  <input
                    type="password"
                    value={pwForm.current}
                    onChange={e => setPwForm(prev => ({ ...prev, current: e.target.value }))}
                    className="mt-1 w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    placeholder="Enter current password"
                    data-testid="current-password-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">New Password</label>
                  <input
                    type="password"
                    value={pwForm.newPw}
                    onChange={e => setPwForm(prev => ({ ...prev, newPw: e.target.value }))}
                    className="mt-1 w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    placeholder="At least 6 characters"
                    data-testid="new-password-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Confirm New Password</label>
                  <input
                    type="password"
                    value={pwForm.confirm}
                    onChange={e => setPwForm(prev => ({ ...prev, confirm: e.target.value }))}
                    className="mt-1 w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    placeholder="Re-enter new password"
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-5">
                <button
                  onClick={() => { setShowChangePassword(false); setPwForm({ current: '', newPw: '', confirm: '' }); }}
                  className="flex-1 px-4 py-2 border rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
                  data-testid="cancel-password-btn"
                >
                  Cancel
                </button>
                <button
                  onClick={handleChangePassword}
                  disabled={pwLoading || !pwForm.current || !pwForm.newPw || !pwForm.confirm}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
                  data-testid="save-password-btn"
                >
                  {pwLoading ? 'Saving...' : 'Change Password'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <main className="main-content">
          {children}
        </main>

        {/* Footer */}
        {footerText && (
          <footer className="no-print" style={{ textAlign: 'center', padding: '16px 0 12px', color: '#94a3b8', fontSize: '12px', borderTop: '1px solid #e2e8f0' }}>
            <span data-testid="app-footer">{footerText}</span>
          </footer>
        )}
      </div>
    </div>
  );
};
